import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Loader2, PanelRightOpen } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { APIProvider } from "@vis.gl/react-google-maps";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useGetCustomV1Bo } from "@/api/customs.api";
import { toast } from "react-toastify";
import { usePreFinalizeFedexQuotation, usePreFinalizeQuotation } from "@/api/expedition.api";
import { useGetAllProducts } from "@/api/product.api";
import { useGetAllProductsUsaByCompany } from "@/api/productUsa.api";
import {
  preFinalizeFedexQuotationPayload,
  preFinalizeWoApiQuotationPayload,
  QuotationUpdateBoDefaultValues,
} from "@/components/backOffice/expeditionForm/expeditionFormHelper";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  QuotationUpdateBoFormData,
  QuotationUpdateBoFormSchema,
} from "@/types/forms/bo/expeditionBo.form";
import { ExpeditionFormStepProps } from "@/components/backOffice/expeditionForm/expeditionForm.types";
import { ExpeditionHeader } from "@/components/backOffice/expeditionForm/expeditionHeader";
import { ApiInfo } from "@/components/backOffice/expeditionForm/shared/sections/apiInfo/apiInfo";
import { DeliveryContent } from "@/components/backOffice/expeditionForm/shared/sections/deliveryContent/deliveryContent";
import { ExpeditionDeliveryInfo } from "@/components/backOffice/expeditionForm/shared/sections/deliveryInfo/deliveryInfo";
import { Packaging } from "@/components/backOffice/expeditionForm/shared/sections/packaging/packaging";
import { PriceUpdate } from "@/components/backOffice/expeditionForm/shared/sections/priceUpdate";
import {
  SideContent,
  SideContentMobileModal,
} from "@/components/backOffice/expeditionForm/shared/sections/sidePanel/sideContent";

export const QuotationExpeditionFormStepOne = ({
  expedition,
  company,
  apiEnum,
  formStep,
  setFormStep,
  onApiEnumConfirmed,
}: ExpeditionFormStepProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useForm<QuotationUpdateBoFormData>({
    resolver: zodResolver(QuotationUpdateBoFormSchema),
    defaultValues: QuotationUpdateBoDefaultValues({ expedition, company, apiEnum }),
  });
  const { mutateAsync: preFinalizeQuotation, isPending: isPreFinalizingQuotation } =
    usePreFinalizeQuotation();
  const { mutateAsync: preFinalizeFedexQuotation, isPending: isPreFinalizingFedexQuotation } =
    usePreFinalizeFedexQuotation();
  const isPending = isPreFinalizingQuotation || isPreFinalizingFedexQuotation;

  const [isSideContentOpen, setIsSideContentOpen] = useState(false);

  const importerId = useWatch({ control: methods.control, name: "importerId" });

  const {
    data: products,
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
  } = useGetAllProducts();
  const {
    data: productsUSA,
    isLoading: isLoadingProductsUSA,
    isError: isErrorProductsUSA,
  } = useGetAllProductsUsaByCompany(company.id, importerId);
  const {
    data: custom,
    isLoading: isLoadingCustoms,
    isError: isErrorCustoms,
  } = useGetCustomV1Bo(expedition.quotation?.customId ?? "");

  if (isLoadingProducts || isLoadingProductsUSA || isLoadingCustoms) {
    return <Loader2 className="animate-spin" />;
  }

  if (isErrorProducts || isErrorProductsUSA || isErrorCustoms) {
    return <div>{t("formStepOne.loadError")}</div>;
  }

  const productsList = expedition.deliveryInfo?.country === "US" ? productsUSA : products?.data;
  const isUsaProducts = expedition.deliveryInfo?.country === "US";

  const onSubmit = async (data: QuotationUpdateBoFormData) => {
    if (expedition.notification?.some((n) => n.readBy == null)) {
      toast.error(t("formStepOne.unreadNotifications"), { position: "top-center" });
      return;
    }
    if (data.apiEnum === "FEDEX") {
      const payload = preFinalizeFedexQuotationPayload({
        data: data as Extract<QuotationUpdateBoFormData, { apiEnum: "FEDEX" }>,
      });

      await preFinalizeFedexQuotation({
        id: expedition.id,
        payload: payload,
      });
    } else {
      const payload = preFinalizeWoApiQuotationPayload({
        data: data as Extract<QuotationUpdateBoFormData, { apiEnum: "NONE" }>,
      });

      await preFinalizeQuotation({
        id: expedition.id,
        payload: payload,
      });
    }

    onApiEnumConfirmed?.(data.apiEnum);
    setFormStep(2);
  };

  return (
    <APIProvider apiKey="AIzaSyDIEG5sck092TlO8WTxyEQjawmwtPMf3vw">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
          className="@container absolute inset-0 flex flex-col overflow-hidden"
        >
          <ExpeditionHeader expedition={expedition} formStep={formStep} setStep={setFormStep} />
          <div className="relative flex w-full flex-col justify-between gap-2 overflow-hidden @min-[896px]:flex-row">
            <Accordion
              type="multiple"
              className="@container flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4"
              defaultValue={[
                "deliveryInfo",
                "deliveryContent",
                "packaging",
                "apiInfo",
                "pickupAndBilling",
              ]}
            >
              <ExpeditionDeliveryInfo expedition={expedition} custom={custom} />
              <DeliveryContent
                products={productsList}
                isUsaProducts={isUsaProducts}
                companyId={company.id}
                countryCode={expedition.deliveryInfo?.country ?? ""}
                viaImportedInvoice={expedition.quotation?.viaImportedInvoice || false}
              />
              <Packaging />
              <ApiInfo expedition={expedition} company={company} />
              <PriceUpdate expedition={expedition} />
              <Button type="submit" className="w-fit self-start" disabled={isPending}>
                {isPending ? <Loader2 className="animate-spin" /> : t("formStepOne.submit")}
              </Button>
            </Accordion>
            <div className="hidden min-h-0 w-[440px] flex-col gap-4 overflow-y-auto p-4 @min-[896px]:flex">
              <SideContent expedition={expedition} company={company} custom={custom} />
            </div>
            <Button
              type="button"
              variant="default"
              onClick={() => setIsSideContentOpen(true)}
              aria-label={t("formStepOne.openDetails")}
              className="absolute right-4 bottom-4 z-30 flex h-12 w-12 items-center justify-center rounded-full shadow-lg @min-[896px]:hidden [&_svg]:h-6 [&_svg]:w-6"
            >
              <PanelRightOpen />
            </Button>
            <SideContentMobileModal
              open={isSideContentOpen}
              onClose={() => setIsSideContentOpen(false)}
              expedition={expedition}
              company={company}
              custom={custom}
            />
          </div>
        </form>
      </FormProvider>
    </APIProvider>
  );
};
